<template>
  <v-data-table
    :items="contracts"
    :headers="headers"
    :loading="loading"
    :footer-props="{
      'items-per-page-options': [10, 20, 30, 40, 50, -1]
    }"
    :search="search"
    :options.sync="options"
    item-key="contract_number"
    class="elevation-1"
  >
    <template v-for="(col, i) in filters" v-slot:[`header.${i}`]="{ header }">
      <div style="display: inline-block; padding: 16px 0;">{{ header.text }}</div>
      <div style="float: right; margin-top: 8px">
        <v-menu :close-on-content-click="false" :nudge-width="200" offset-y max-height="600px" transition="slide-y-transition" left fixed style="position: absolute; right: 0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="indigo" icon v-bind="attrs" v-on="on">
              <v-icon small 
                :color="activeFilters[header.value] && activeFilters[header.value].length < filters[header.value].length ? 'red' : 'default'">
                mdi-filter-variant
              </v-icon>
            </v-btn>
          </template>
          <v-list flat dense class="pa-0">
            <v-list-item-group multiple v-model="activeFilters[header.value]" class="py-2">
              <template v-for="(item, i) in filters[header.value]">
                <v-list-item :key="`${item}`" :value="item" :ripple="false">
                  <template v-slot:default="{ active, toggle }">
                    <v-list-item-action>
                      <v-checkbox :input-value="active" :true-value="item"
                        color="primary" :ripple="false" dense></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title v-if="header.value === 'region_id'">{{ getRegion(item).region_short }}</v-list-item-title>
                      <v-list-item-title v-else-if="header.value === 'assyst_software_id'">{{ getAssystSoftware(item).assyst_version }}</v-list-item-title>
                      <v-list-item-title v-else-if="header.value === 'ipaas_software_id'">{{ getIpaasSoftware(item).ipaas_version }}</v-list-item-title>
                      <v-list-item-title v-else-if="header.value === 'itom_software_id'">{{ getItomSoftware(item).itom_version }}</v-list-item-title>
                      <v-list-item-title v-else v-text="item"></v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </template>
            </v-list-item-group>
            <v-divider></v-divider>
            <v-row no-gutters>
              <v-col cols="6">
                <v-btn text block @click="toggleAll(header.value)" color="success">Toggle all</v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn text block @click="clearAll(header.value)" color="warning">Clear all</v-btn>
              </v-col>
            </v-row>
          </v-list>
        </v-menu>
      </div>
    </template>
    <template v-slot:top>
      <v-row>
        <v-col
          class="col-8"
        >
          <v-text-field
            v-model="search"
            label="Search"
            class="mx-4"
          />
        </v-col>
        <v-spacer />
        <v-col class="mb-2">
          <v-btn @click="resetFilters()" color="primary">Reset Filters</v-btn>
        </v-col>
        <v-col class="mb-2">
          <v-btn @click="exportTableToCSV()" color="primary">Export to CSV</v-btn>
        </v-col>
      </v-row>
    </template>
    <template v-slot:item.contract_name="{ item }">
      <router-link
        v-if="(userHasPermission('view_logs'))"
        :to="`${item.contract_id}`"
        class="text-decoration-none"
      >
        {{ item.contract_name }}
      </router-link>
      <span v-else>
        {{ item.contract_name }}
      </span>
    </template>
    <template v-slot:item.region_id="{ item }" v-if="regions.length > 0">
          {{ getRegion(item.region_id).region_short }}
        </template>
    <template v-slot:item.assyst_software_id="{ item }">
        <v-chip
          v-if="item.assyst_software_id !== null"
          :to="`/software/assyst/${item.assyst_software_id}`"
          :color="softwareChipColour(getAssystSoftware(item.assyst_software_id).expiry_date)"
        >
          {{ getAssystSoftware(item.assyst_software_id).assyst_version }}
        </v-chip>
    </template>
    <template v-slot:item.ipaas_software_id="{ item }">
        <v-chip
          v-if="item.ipaas_software_id !== null"
          :to="`/software/ipaas/${item.ipaas_software_id}`"
          :color="softwareChipColour(getIpaasSoftware(item.ipaas_software_id).expiry_date)"
        >
          {{ getIpaasSoftware(item.ipaas_software_id).ipaas_version }}
        </v-chip>
    </template>
    <template v-slot:item.itom_software_id="{ item }">
        <v-chip
          v-if="item.itom_software_id !== null"
          :to="`/software/itom/${item.itom_software_id}`"
          :color="softwareChipColour(getItomSoftware(item.itom_software_id).expiry_date)"
        >
          {{ getItomSoftware(item.itom_software_id).itom_version }}
        </v-chip>
    </template>
    <template v-slot:item.assyst_url="{ item }">
      <v-tooltip
        v-if="item.assyst_software_id !== null && getUrl(item.contract_id, 'assyst') !== undefined"
        top
        color="accent"
      >
        <template
          v-slot:activator="{ on, attrs }"
        >
          <v-btn
            icon
            color="accent"
            :href="`https://${getUrl(item.contract_id, 'assyst').url}`"
            target="_blank"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-web</v-icon>
          </v-btn>
        </template>
        <span> {{ getUrl(item.contract_id, 'assyst').url }}</span>
      </v-tooltip>
    </template>
    <template v-slot:item.database_type="{ item }">
      <v-tooltip
        v-if="item.database_type === 'Azure SQL'"
        top
        color="accent"
      >
        <template
          v-slot:activator="{ on, attrs }"
        >
          <v-chip
            color="accent"
            v-bind="attrs"
            v-on="on"
          >
            Azure SQL
          </v-chip>
        </template>
        <span>{{ getSqlLoadBalancer(item.sql_load_balancer_id).sql_load_balancer_ip }}</span>
      </v-tooltip>
      <v-tooltip
        v-else
        top
        color="secondary"
      >
        <template
          v-slot:activator="{ on, attrs }"
        >
          <v-chip
            color="secondary"
            v-bind="attrs"
            v-on="on"
          >
            IaaS
          </v-chip>
        </template>
        <span
          v-if="item.sql_load_balancer_id"
        >
          {{ getSqlLoadBalancer(item.sql_load_balancer_id).primary_server_id !== null ? getSqlServer(getSqlLoadBalancer(item.sql_load_balancer_id).primary_server_id).sql_server_name : "Unknown" }} | {{ getSqlLoadBalancer(item.sql_load_balancer_id).secondary_server_id !== null ? getSqlServer(getSqlLoadBalancer(item.sql_load_balancer_id).secondary_server_id).sql_server_name : "Unknown" }}
        </span>
        <span
          v-else
        >
          Unknown
        </span>
      </v-tooltip>
    </template>
    <template v-slot:item.itom_url="{ item }">
      <v-tooltip
        v-if="item.itom_software_id !== null && getUrl(item.contract_id, 'itom') !== undefined"
        top
        color="secondary"
      >
        <template
          v-slot:activator="{ on, attrs }"
        >
          <v-btn
            icon
            color="secondary"
            :href="`https://${getUrl(item.contract_id, 'itom').url}`"
            target="_blank"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-web</v-icon>
          </v-btn>
        </template>
        <span> {{ getUrl(item.contract_id, 'itom').url }}</span>
      </v-tooltip>
    </template>
  </v-data-table>
</template>

<script>
  import { get } from 'vuex-pathify'
  export default {
    name: 'ContractsTable',
    props: {
      contracts: Array,
    },
    data () {
      return {
        headers: [
          { value: 'contract_name', text: 'Customer', width: '20%'},
          { value: 'contract_number', text: 'Contract' },
          {
            value: 'region_id',
            text: 'Region',
            sortable: true,
            filter: value => {
              return this.activeFilters.region_id ? this.activeFilters.region_id.includes(value) : true
            },
          },
          {
            value: 'status',
            text: 'Status',
            sortable: true,
            filter: value => {
              return this.activeFilters.status ? this.activeFilters.status.includes(value) : true
            },
          },
          {
            value: 'authentication',
            text: 'Authentication',
            sortable: true,
            filter: value => {
              return this.activeFilters.authentication ? this.activeFilters.authentication.includes(value) : true
            },
          },
          {
            value: 'assyst_software_id',
            text: 'Assyst',
            sortable: true,
            filter: value => {
              return this.activeFilters.assyst_software_id ? this.activeFilters.assyst_software_id.includes(value) : true
            },
          },
          {
            value: 'ipaas_software_id',
            text: 'iPaaS',
            sortable: true,
            filter: value => {
              return this.activeFilters.ipaas_software_id ? this.activeFilters.ipaas_software_id.includes(value) : true
            },
          },
          {
            value: 'itom_software_id',
            text: 'ITOM',
            sortable: true,
            filter: value => {
              return this.activeFilters.itom_software_id ? this.activeFilters.itom_software_id.includes(value) : true
            },
          },
          {
            value: 'database_type',
            text: 'Database',
            sortable: true,
            filter: value => {
              return this.activeFilters.database_type ? this.activeFilters.database_type.includes(value) : true
            },
          },
          { value: 'assyst_url', text: 'URL', sortable: false },
          { value: 'itom_url', text: 'ITOM', sortable: false },
        ],
        options: {
          itemsPerPage: -1,
          sortBy: ['contract_name'],
        },
        search: '',
        filters: { 'status': [], 'authentication': [], 'region_id': [], 'assyst_software_id': [], 'ipaas_software_id': [], 'itom_software_id': [], 'database_type': [] },
        activeFilters: {'status': [], 'authentication': [], 'region_id': [], 'assyst_software_id': [], 'ipaas_software_id': [], 'itom_software_id': [], 'database_type': [] },
        totalRows: 300,
        loading: true,
      }
    },
    computed: {
      ...get('user', [
        'user',
      ]),
      ...get('settings', [
        'regions',
        'sql_servers',
        'sql_load_balancers',
        'customer_urls'
      ]),
      ...get('software', [
        'assyst',
        'ipaas',
        'itom'
      ]),
    },
    watch: {
      options: {
        handler () {
          this.updateSettings()
        },
      },
      contracts: {
        handler () {
          this.initFilters()
        }
      },
      activeFilters: {
        handler () {
          this.updateFilters()
        },
        deep: true
      },
      search: {
        handler () {
          this.updateSearch()
        }
      }
    },
    mounted () {
      const itemsPerPage = localStorage.getItem('contract_items_per_page')
      const savedFilters = localStorage.getItem('contract_filters')
      const searchTerm = localStorage.getItem('contract_search')

      if (itemsPerPage) {
        this.options.itemsPerPage = parseInt(itemsPerPage)
      }

      if (searchTerm) {
        this.search = searchTerm
      }

      this.initFilters()

      if (savedFilters) {
        this.activeFilters = JSON.parse(savedFilters)
      }
    },
    methods: {
      userHasPermission: function (perm) {
        let perms = this.user.permissions.map(a => a.name)
        return perms.includes(perm)
      },
      getRegion: function (region_id) {
        let region = this.regions.find(element => element.region_id === region_id)
        return region
      },
      getAssystSoftware: function (software_id) {
        let version = this.assyst.find(element => element.assyst_software_id === software_id)
        return version
      },
      getIpaasSoftware: function (software_id) {
        let version = this.ipaas.find(element => element.ipaas_software_id === software_id)
        return version
      },
      getItomSoftware: function (software_id) {
        let version = this.itom.find(element => element.itom_software_id === software_id)
        return version
      },
      getSqlLoadBalancer: function (sql_lb_id) {
        let lb = this.sql_load_balancers.find(element => element.sql_load_balancer_id === sql_lb_id)
        return lb
      },
      getSqlServer: function (sql_server_id) {
        let server = this.sql_servers.find(element => element.sql_server_id === sql_server_id)
        return server
      },
      getUrl: function (contract_id, type) {
        let url = this.customer_urls.find(element => element.contract_id === contract_id && element.application === type)
        return url
      },
      updateSettings () {
        localStorage.setItem(
          'contract_items_per_page',
          this.options.itemsPerPage.toString(),
        )
      },
      updateFilters () {
        localStorage.setItem(
          'contract_filters',
          JSON.stringify(this.activeFilters),
        )
      },
      updateSearch () {
        localStorage.setItem(
          'contract_search',
          this.search,
        )
      },
      resetFilters () {
        localStorage.removeItem('contract_filters')
        this.search = ''
        this.initFilters()
      },
      initFilters() {
        const savedFilters = localStorage.getItem('contract_filters')
        for (var col in this.filters) {
          this.filters[col] = this.contracts.map((d) => { return d[col] }).filter(
            (value, index, self) => { return self.indexOf(value) === index }
          )

          // Apply alphabetical sorting dependant on filter type. Reverse for software versions ensures newest at top
          if (col === 'assyst_software_id' || col === 'ipaas_software_id' || col === 'itom_software_id') {
            this.filters[col].sort((a, b) => a - b).reverse()
          } else {
            this.filters[col].sort()
          }

        }

        if (savedFilters) {
          this.activeFilters = JSON.parse(savedFilters)
        } else {
          this.activeFilters = Object.assign({}, this.filters)

          // Hide 'decommissioned' contracts by default, if enabled
          var ind = this.activeFilters['status'].indexOf('Decommissioned')
          if (ind !== -1) {
            const slice = this.activeFilters['status'].slice()
            slice.splice(ind, 1)
            this.activeFilters['status'] = slice
          }

          // Hide 'DR' contracts by default, if enabled
          ind = this.activeFilters['status'].indexOf('DR')
          if (ind !== -1) {
            const slice = this.activeFilters['status'].slice()
            slice.splice(ind, 1)
            this.activeFilters['status'] = slice
          }
        }
        
        if (this.contracts.length > 0) {
          this.loading = false
        }
      },
      toggleAll (col) {
        this.activeFilters[col] = this.contracts.map((d) => { return d[col] }).filter(
          (value, index, self) => { return self.indexOf(value) === index }
        )
      },
      
      clearAll (col) {
        this.activeFilters[col] = []
      },
      downloadCSV(csv, filename) {
        var csvFile
        var downloadLink

        csvFile = new Blob([csv], {type: "text/csv"})

        downloadLink = document.createElement("a")
        downloadLink.download = filename
        downloadLink.href = window.URL.createObjectURL(csvFile)
        downloadLink.style.display = "none"
        document.body.appendChild(downloadLink)
        downloadLink.click()
      },
      exportTableToCSV() {
        var csv = []
        var filename = 'saas_contracts_' + Date.now() + '.csv'
        var rows = document.querySelectorAll("table tr")
    
        for (var i = 0; i < rows.length; i++) {
            var row = [], cols = rows[i].querySelectorAll("td, th")
            
            for (var j = 0; j < cols.length; j++) 
                row.push(cols[j].innerText)
            
            csv.push(row.join(","))     
        }

        // Download CSV file
        this.downloadCSV(csv.join("\n"), filename)
      },
      softwareChipColour: function (date) {
        let currentDate = new Date()
        let softwareEOLDate = new Date(date)
        let softwareNearEOLDate = new Date(softwareEOLDate.getFullYear(), softwareEOLDate.getMonth() - 6, softwareEOLDate.getDate())

        if (currentDate > softwareEOLDate) {
          return 'error'
        } else if (currentDate > softwareNearEOLDate) {
          return 'warning'
        } else {
          return 'success'
        }
      },
    },
  }
</script>
